
import { supabase } from "@/lib/supabase/client";
import { DetectionResult } from "./types";
import { monitorApiCall } from "@/utils/monitoring";

/**
 * Analyze voice for synthetic manipulation
 */
export async function analyzeVoice(file: File): Promise<DetectionResult> {
  try {
    // Convert file to base64
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Call the voice analysis edge function
    const { data, error } = await supabase.functions.invoke('detect-voice', {
      body: { 
        audioData: base64,
        fileName: file.name,
        options: {
          useRawWaveform: file.size < 5000000 // Use raw waveform analysis for smaller files
        }
      }
    });

    if (error) throw error;

    return {
      score: data.score,
      confidence: data.confidence,
      flags: data.flags,
      explanation: data.explanation,
      modelName: data.modelName,
      timestamp: new Date(data.timestamp)
    };
  } catch (error) {
    console.error("Voice analysis error:", error);
    throw error;
  }
}

/**
 * Analyze image for AI generation and manipulation
 */
export async function analyzeImage(file: File): Promise<DetectionResult> {
  try {
    console.log("Starting image analysis process...");
    
    // Convert file to base64
    const base64 = await fileToBase64(file);
    
    // Remove the Data URL prefix (e.g., "data:image/jpeg;base64,")
    const base64Data = base64.split(',')[1];
    
    console.log(`Image converted to base64, size: ${base64Data.length} chars`);
    
    // Call the image analysis edge function with advanced options
    const { data, error } = await supabase.functions.invoke('analyze-image', {
      body: { 
        imageData: base64Data,
        fileName: file.name,
        options: {
          detectAIGeneration: true,
          deepAnalysis: true,
          checkMetadata: true,
          detectManipulation: true
        }
      }
    });

    if (error) {
      console.error("Error from analyze-image function:", error);
      throw error;
    }

    console.log("Image analysis complete, result:", data);
    
    return {
      score: data.score,
      confidence: data.confidence,
      flags: data.flags,
      explanation: data.explanation,
      modelName: data.modelName,
      timestamp: new Date(data.timestamp)
    };
  } catch (error) {
    console.error("Image analysis error:", error);
    throw error;
  }
}

/**
 * Convert a file to base64 encoding
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
